import React, { useState } from 'react';
import { X, Flag, AlertTriangle, Send } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'inappropriate', label: 'Inappropriate Content' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'other', label: 'Other' }
];

function ReportModal({ isOpen, onClose, contentType = 'comment', contentId, contentPreview }) {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const typeLabel = contentType === 'photo' ? 'Photo' : 'Comment';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason) {
      setError('Please select a reason for reporting');
      return;
    }

    if (!user) {
      setError('You must be logged in to report content');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      if (contentType === 'comment' || contentType === 'review') {
        const { error: insertError } = await supabase
          .from('comment_reports')
          .insert({
            comment_id: contentId,
            reported_by_user_id: user.id,
            reason,
            details: details.trim() || null,
            status: 'pending'
          });
        if (insertError) throw insertError;
      } else {
        const { error: insertError } = await supabase
          .from('content_reports')
          .insert({
            content_type: contentType,
            content_id: contentId,
            reported_by_user_id: user.id,
            reason,
            details: details.trim() || null,
            status: 'pending'
          });
        if (insertError) throw insertError;
      }

      setSuccess(true);
    } catch (err) {
      console.error('Error submitting report:', err);
      setError(err.message || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setDetails('');
    setError('');
    setSuccess(false);
    onClose();
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-dive-dark border border-dive-gray rounded-xl max-w-md w-full p-6 text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Flag className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-white text-lg font-medium mb-2">Report Submitted</h3>
          <p className="text-gray-400 text-sm mb-6">
            Thank you for reporting. Our team will review this {typeLabel.toLowerCase()} and take appropriate action within 24 hours.
          </p>
          <button onClick={handleClose} className="btn-primary">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-dive-dark border border-dive-gray rounded-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dive-gray">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Flag className="w-5 h-5 text-red-400" />
            Report {typeLabel}
          </h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Content Preview */}
          {contentPreview && (
            <div className="bg-dive-darker border border-dive-gray rounded-lg p-3">
              <p className="text-gray-400 text-xs mb-1">{typeLabel} being reported:</p>
              <p className="text-white text-sm line-clamp-3">{contentPreview}</p>
            </div>
          )}

          {/* Reason Selection */}
          <div>
            <label className="text-gray-400 text-sm mb-2 block">Reason for reporting *</label>
            <div className="space-y-2">
              {REPORT_REASONS.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    reason === r.value
                      ? 'bg-dive-neon/10 border-dive-neon text-white'
                      : 'border-dive-gray text-gray-400 hover:border-dive-light'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={(e) => setReason(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    reason === r.value ? 'border-dive-neon' : 'border-gray-500'
                  }`}>
                    {reason === r.value && (
                      <div className="w-2 h-2 rounded-full bg-dive-neon" />
                    )}
                  </div>
                  <span className="text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          <div>
            <label className="text-gray-400 text-sm mb-2 block">Additional details (optional)</label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Provide any additional context..."
              rows={3}
              className="w-full resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !reason}
              className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReportModal;
